import { useMemo, useState, useCallback } from 'react';
import {
  calendarPayloadToEvents,
  tasksToEvents,
  startOfMonth,
  addMonths,
  sameDay,
  daysInMonthGrid,
  eventOverlapsDay,
  formatDateOnly,
  parseDateOnly,
} from '../utils/calendarUtils.js';
import { api } from '../api/client.js';
import { t, getLang } from '../i18n/index.jsx';
import { Empty } from './ui.jsx';
import { useToast } from './feedback.jsx';

function monthTitle(d) {
  return d.toLocaleDateString(getLang() === 'it' ? 'it-IT' : 'en-GB', { month: 'long', year: 'numeric' });
}

function dayTitle(d) {
  return d.toLocaleDateString(getLang() === 'it' ? 'it-IT' : 'en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
  });
}

const MS_DAY = 86400000;

/**
 * Native month + agenda calendar.
 * Task pills can be dragged onto another day when canEdit (preserves duration).
 */
export default function AppCalendar({
  data,
  tasksOnly = false,
  filters,
  canEdit = false,
  onEventOpen,
  onDatesChanged,
  defaultView = 'Month',
}) {
  const toast = useToast();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [view, setView] = useState(defaultView === 'Agenda' ? 'agenda' : 'month');
  const [dropDay, setDropDay] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const today = useMemo(() => new Date(), []);

  const events = useMemo(() => {
    if (tasksOnly) return tasksToEvents(data);
    return calendarPayloadToEvents(data, filters);
  }, [data, tasksOnly, filters]);

  const cells = useMemo(() => daysInMonthGrid(cursor), [cursor]);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    cells.forEach((day) => {
      const key = formatDateOnly(day);
      map.set(key, events.filter((ev) => eventOverlapsDay(ev, day)));
    });
    return map;
  }, [cells, events]);

  const agenda = useMemo(() => {
    const monthStart = startOfMonth(cursor);
    const monthEnd = addMonths(cursor, 1);
    monthEnd.setMilliseconds(-1);
    const inMonth = events
      .filter((ev) => ev.StartTime <= monthEnd && ev.EndTime >= monthStart)
      .sort((a, b) => a.StartTime - b.StartTime);
    const groups = [];
    let curKey = null;
    let bucket = null;
    inMonth.forEach((ev) => {
      const key = formatDateOnly(ev.StartTime);
      if (key !== curKey) {
        curKey = key;
        bucket = { key, date: ev.StartTime, items: [] };
        groups.push(bucket);
      }
      bucket.items.push(ev);
    });
    return groups;
  }, [events, cursor]);

  const weekdays = getLang() === 'it'
    ? ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const rescheduleTask = useCallback(async (ev, targetDayKey) => {
    if (!canEdit || ev.EventType !== 'task' || busy) return;
    const newStart = parseDateOnly(targetDayKey);
    if (!newStart) return;
    const oldStart = new Date(ev.StartTime);
    oldStart.setHours(0, 0, 0, 0);
    const oldEnd = new Date(ev.EndTime);
    oldEnd.setHours(0, 0, 0, 0);
    const durationDays = Math.max(0, Math.round((oldEnd - oldStart) / MS_DAY));
    const newEnd = new Date(newStart);
    newEnd.setDate(newEnd.getDate() + durationDays);
    const startStr = formatDateOnly(newStart);
    const endStr = formatDateOnly(newEnd);
    if (startStr === formatDateOnly(oldStart) && endStr === formatDateOnly(oldEnd)) return;

    setBusy(true);
    try {
      await api.patch(`/tasks/${ev.RawId}/dates`, {
        start_date: startStr,
        due_date: endStr,
      });
      toast(t('Dates updated'));
      onDatesChanged?.();
    } catch (e) {
      toast(e.message || t('Could not update dates'), { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }, [canEdit, busy, onDatesChanged, toast]);

  function onPillDragStart(e, ev) {
    if (!canEdit || ev.EventType !== 'task') {
      e.preventDefault();
      return;
    }
    setDraggingId(ev.Id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ev.Id);
    e.dataTransfer.setData('application/x-task-event', JSON.stringify({
      id: ev.Id,
      rawId: ev.RawId,
      start: formatDateOnly(ev.StartTime),
      end: formatDateOnly(ev.EndTime),
      type: ev.EventType,
      subject: ev.Subject,
    }));
  }

  function onPillDragEnd() {
    setDraggingId(null);
    setDropDay(null);
  }

  function onCellDragOver(e, dayKey) {
    if (!canEdit || !draggingId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropDay !== dayKey) setDropDay(dayKey);
  }

  function onCellDrop(e, dayKey) {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/x-task-event');
    setDropDay(null);
    setDraggingId(null);
    if (!raw) return;
    try {
      const meta = JSON.parse(raw);
      const ev = events.find((x) => x.Id === meta.id);
      if (ev) rescheduleTask(ev, dayKey);
    } catch { /* ignore */ }
  }

  return (
    <div className={`app-cal panel ${busy ? 'is-busy' : ''}`}>
      <div className="app-cal-toolbar">
        <div className="app-cal-nav">
          <button type="button" className="btn ghost sm" onClick={() => setCursor((c) => addMonths(c, -1))} aria-label={t('Previous')}>‹</button>
          <button type="button" className="btn ghost sm" onClick={() => setCursor(startOfMonth(new Date()))}>{t('Today')}</button>
          <button type="button" className="btn ghost sm" onClick={() => setCursor((c) => addMonths(c, 1))} aria-label={t('Next')}>›</button>
          <h2 className="app-cal-title">{monthTitle(cursor)}</h2>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {canEdit && view === 'month' && (
            <span className="muted sm">{t('Drag a task onto a day to reschedule')}</span>
          )}
          <div className="sheet-tabs" style={{ margin: 0 }}>
            <button type="button" className={`chip ${view === 'month' ? 'on' : ''}`} onClick={() => setView('month')}>{t('Month')}</button>
            <button type="button" className={`chip ${view === 'agenda' ? 'on' : ''}`} onClick={() => setView('agenda')}>{t('Agenda')}</button>
          </div>
        </div>
      </div>

      {view === 'month' ? (
        <div className="app-cal-month">
          <div className="app-cal-weekdays">
            {weekdays.map((d) => <div key={d} className="app-cal-wd">{d}</div>)}
          </div>
          <div className="app-cal-grid">
            {cells.map((day) => {
              const key = formatDateOnly(day);
              const dayEvents = eventsByDay.get(key) || [];
              const inMonth = day.getMonth() === cursor.getMonth();
              const isToday = sameDay(day, today);
              const isDrop = dropDay === key;
              return (
                <div
                  key={key}
                  className={`app-cal-cell ${inMonth ? '' : 'muted-month'} ${isToday ? 'today' : ''} ${isDrop ? 'drop-target' : ''}`}
                  onDragOver={(e) => onCellDragOver(e, key)}
                  onDragLeave={() => { if (dropDay === key) setDropDay(null); }}
                  onDrop={(e) => onCellDrop(e, key)}
                >
                  <div className="app-cal-daynum">{day.getDate()}</div>
                  <div className="app-cal-events">
                    {dayEvents.slice(0, 3).map((ev) => {
                      const draggable = canEdit && ev.EventType === 'task';
                      return (
                        <button
                          key={ev.Id}
                          type="button"
                          className={`app-cal-pill ${draggable ? 'draggable' : ''} ${draggingId === ev.Id ? 'dragging' : ''}`}
                          style={{ background: ev.CategoryColor || 'var(--primary)' }}
                          title={draggable ? `${ev.Subject} — ${t('Drag to reschedule')}` : ev.Subject}
                          draggable={draggable}
                          onDragStart={(e) => onPillDragStart(e, ev)}
                          onDragEnd={onPillDragEnd}
                          onClick={(e) => {
                            // avoid open on accidental drag end
                            if (e.defaultPrevented) return;
                            onEventOpen?.(ev);
                          }}
                        >
                          {ev.Subject}
                        </button>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <button
                        type="button"
                        className="app-cal-more muted sm"
                        onClick={() => setView('agenda')}
                      >
                        +{dayEvents.length - 3}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="app-cal-agenda">
          {agenda.length === 0 ? (
            <Empty>{t('No events this month.')}</Empty>
          ) : (
            agenda.map((g) => (
              <div key={g.key} className="app-cal-agenda-group">
                <div className="app-cal-agenda-date">{dayTitle(g.date)}</div>
                <ul className="app-cal-agenda-list">
                  {g.items.map((ev) => (
                    <li key={ev.Id}>
                      <button type="button" className="app-cal-agenda-item" onClick={() => onEventOpen?.(ev)}>
                        <span className="app-cal-dot" style={{ background: ev.CategoryColor }} />
                        <span className="app-cal-agenda-main">
                          <span className="ll-title">{ev.Subject}</span>
                          <span className="muted sm">
                            {[ev.ProjectName, ev.ClientName, ev.EventType].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
